import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { formatDateTime } from "@/lib/i18n";

type MediaDeviceOption = {
  deviceId: string;
  label: string;
};

export default function BackstagePage() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("loading");
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [isGoingLive, setIsGoingLive] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [remindersStatus, setRemindersStatus] = useState<"idle" | "creating" | "done" | "error">("idle");
  const [videoDevices, setVideoDevices] = useState<MediaDeviceOption[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceOption[]>([]);
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState("");
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState("");
  const [isTestingEquipment, setIsTestingEquipment] = useState(false);
  const [equipmentError, setEquipmentError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const previewRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number>(0);

  const audioBars = useMemo(
    () => Array.from({ length: 10 }, (_, index) => Math.max(0.15, Math.min(1, audioLevel - index * 0.08))),
    [audioLevel],
  );

  const stopEquipmentTest = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = 0;
    }
    analyserRef.current = null;

    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }

    if (previewRef.current) {
      previewRef.current.srcObject = null;
    }

    setIsTestingEquipment(false);
    setAudioLevel(0);
  }, []);

  const syncDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setVideoDevices([]);
      setAudioDevices([]);
      setCameraReady(false);
      setMicReady(false);
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const nextVideoDevices = devices
        .filter((device) => device.kind === "videoinput")
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${index + 1}`,
        }));
      const nextAudioDevices = devices
        .filter((device) => device.kind === "audioinput")
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${index + 1}`,
        }));

      setVideoDevices(nextVideoDevices);
      setAudioDevices(nextAudioDevices);
      setCameraReady(nextVideoDevices.length > 0);
      setMicReady(nextAudioDevices.length > 0);
      setSelectedVideoDeviceId((current) => current || nextVideoDevices[0]?.deviceId || "");
      setSelectedAudioDeviceId((current) => current || nextAudioDevices[0]?.deviceId || "");
    } catch {
      setVideoDevices([]);
      setAudioDevices([]);
      setCameraReady(false);
      setMicReady(false);
    }
  }, []);

  const startEquipmentTest = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setEquipmentError("Media device testing is not supported in this browser.");
      return;
    }

    stopEquipmentTest();
    setEquipmentError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedVideoDeviceId ? { deviceId: { exact: selectedVideoDeviceId } } : true,
        audio: selectedAudioDeviceId ? { deviceId: { exact: selectedAudioDeviceId } } : true,
      });

      streamRef.current = stream;
      if (previewRef.current) {
        previewRef.current.srcObject = stream;
        void previewRef.current.play().catch(() => undefined);
      }

      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const updateAudioLevel = () => {
        if (!analyserRef.current) {
          return;
        }

        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const average = data.reduce((sum, value) => sum + value, 0) / Math.max(data.length, 1);
        setAudioLevel(Math.min(1, average / 128));
        animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };

      updateAudioLevel();
      setIsTestingEquipment(true);
      await syncDevices();
    } catch (error) {
      stopEquipmentTest();
      setEquipmentError(error instanceof Error ? error.message : "Unable to access camera or microphone.");
    }
  }, [selectedAudioDeviceId, selectedVideoDeviceId, stopEquipmentTest, syncDevices]);

  useEffect(() => {
    if (!webinarId) return;

    const checkStatus = async () => {
      try {
        const data = await apiFetch<{ status: string; scheduledAt: string | null }>(
          `/v1/webinars/${webinarId}/status`,
        );
        setStatus(data.status);
        setScheduledAt(data.scheduledAt);
      } catch {
        // Retry on next interval
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [webinarId]);

  useEffect(() => {
    void syncDevices();
  }, [syncDevices]);

  useEffect(() => {
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener) {
      return undefined;
    }

    const handleChange = () => {
      void syncDevices();
    };

    mediaDevices.addEventListener("devicechange", handleChange);
    return () => mediaDevices.removeEventListener("devicechange", handleChange);
  }, [syncDevices]);

  useEffect(() => () => stopEquipmentTest(), [stopEquipmentTest]);

  const handleGoLive = async () => {
    if (!webinarId || isGoingLive) return;
    setIsGoingLive(true);

    try {
      await apiFetch(`/v1/webinars/${webinarId}/go-live`, { method: "POST" });
      navigate(`/webinar/${webinarId}/live`);
    } catch {
      setIsGoingLive(false);
    }
  };

  const handleSetupReminders = useCallback(async () => {
    if (!webinarId || remindersStatus === "creating") return;
    setRemindersStatus("creating");
    try {
      const res = await apiFetch<{
        templates: {
          preReminders: { name: string; trigger: Record<string, unknown>; nodes: unknown[]; edges: unknown[]; reentryPolicy: string };
          postFollowUp: { name: string; trigger: Record<string, unknown>; nodes: unknown[]; edges: unknown[]; reentryPolicy: string };
        };
      }>(`/v1/webinars/${webinarId}/reminders/setup`, { method: "POST" });

      for (const template of [res.templates.preReminders, res.templates.postFollowUp]) {
        const workflow = await apiFetch<{ workflow: { id: string } }>("/email-marketing/workflows", {
          method: "POST",
          body: JSON.stringify({
            name: template.name,
            triggers: [template.trigger],
            reentryPolicy: template.reentryPolicy,
          }),
        });

        await apiFetch(`/email-marketing/workflows/${workflow.workflow.id}/publish`, {
          method: "POST",
          body: JSON.stringify({
            nodes: template.nodes,
            edges: template.edges,
            changeSummary: "Auto-generated webinar reminder template",
          }),
        });
      }

      setRemindersStatus("done");
    } catch {
      setRemindersStatus("error");
    }
  }, [remindersStatus, webinarId]);

  const handleEndWebinar = async () => {
    if (!webinarId) return;
    try {
      await apiFetch(`/v1/webinars/${webinarId}/end`, { method: "POST" });
      navigate("/webinars");
    } catch {
      // apiFetch throws ApiError — user sees error state via status polling
    }
  };

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading backstage...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Backstage</h1>
          <p className="text-xs text-muted-foreground">
            Status: <span className="font-medium">{status}</span>
            {scheduledAt && ` | Scheduled: ${formatDateTime(scheduledAt)}`}
          </p>
        </div>
        <div className="flex gap-2">
          {status !== "live" && (
            <button
              onClick={handleGoLive}
              disabled={isGoingLive}
              className="rounded-md bg-[var(--color-success)] px-6 py-2 text-sm font-semibold text-white hover:bg-[var(--color-success)] disabled:opacity-50"
            >
              {isGoingLive ? "Going Live..." : "Go Live"}
            </button>
          )}
          {status === "live" && (
            <button
              onClick={handleEndWebinar}
              className="rounded-md bg-destructive px-6 py-2 text-sm font-semibold text-destructive-foreground hover:bg-destructive/90"
            >
              End Webinar
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-4 p-6 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="mb-1 text-sm font-medium text-foreground">Equipment Check</h2>
              <p className="text-xs text-muted-foreground">
                Run the backstage hardware test before you go live. The preview uses the selected devices and the audio meter reflects the live microphone stream.
              </p>
            </div>
            <button
              type="button"
              onClick={() => void (isTestingEquipment ? stopEquipmentTest() : startEquipmentTest())}
              className="rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent"
            >
              {isTestingEquipment ? "Stop Test" : "Test Camera & Mic"}
            </button>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(16rem,1fr)]">
            <div className="rounded-2xl border border-border bg-[var(--color-surface)] p-3">
              <div className="aspect-video overflow-hidden rounded-xl bg-black">
                <video
                  ref={previewRef}
                  autoPlay
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                />
              </div>
              {!isTestingEquipment ? (
                <p className="mt-3 text-xs text-muted-foreground">Start the test to preview your selected camera feed here.</p>
              ) : null}
            </div>

            <div className="space-y-4">
              <div className="grid gap-3">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-foreground">Camera input</span>
                  <select
                    value={selectedVideoDeviceId}
                    onChange={(event) => setSelectedVideoDeviceId(event.target.value)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                    disabled={videoDevices.length === 0}
                  >
                    {videoDevices.length === 0 ? <option value="">No camera detected</option> : null}
                    {videoDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>{device.label}</option>
                    ))}
                  </select>
                </label>

                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-foreground">Microphone input</span>
                  <select
                    value={selectedAudioDeviceId}
                    onChange={(event) => setSelectedAudioDeviceId(event.target.value)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
                    disabled={audioDevices.length === 0}
                  >
                    {audioDevices.length === 0 ? <option value="">No microphone detected</option> : null}
                    {audioDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>{device.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="rounded-xl border border-border bg-background p-3">
                <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Microphone level</span>
                  <span>{isTestingEquipment ? "Live" : "Idle"}</span>
                </div>
                <div className="flex h-16 items-end gap-1">
                  {audioBars.map((barHeight, index) => (
                    <div
                      key={index}
                      className="flex-1 rounded-t-md bg-[var(--color-primary)]/80 transition-all"
                      style={{ height: `${Math.round(barHeight * 100)}%` }}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className={`h-2 w-2 rounded-full ${cameraReady ? "bg-[var(--color-success)]/70" : "bg-[var(--color-destructive)]/70"}`} />
                  <span className="text-foreground">Camera: {cameraReady ? "Ready" : "Not detected"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`h-2 w-2 rounded-full ${micReady ? "bg-[var(--color-success)]/70" : "bg-[var(--color-destructive)]/70"}`} />
                  <span className="text-foreground">Microphone: {micReady ? "Ready" : "Not detected"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="h-2 w-2 rounded-full bg-[var(--color-success)]/70" />
                  <span className="text-foreground">Network: Connected</span>
                </div>
              </div>

              {equipmentError ? (
                <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-3 py-2 text-sm text-[var(--color-destructive)]">
                  {equipmentError}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Presenter Notes</h2>
          <textarea
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            placeholder="Type your notes here... (shared with co-presenters in G.5)"
            className="h-48 w-full resize-none rounded-md border border-input bg-background p-3 text-sm"
          />
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Attendee Status</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-2xl font-bold text-foreground">0</p>
              <p className="text-xs text-muted-foreground">In Waiting Room</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">0</p>
              <p className="text-xs text-muted-foreground">Registered</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-medium text-foreground">Pre-Launch Checklist</h2>
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" className="rounded border-border" />
              <span className="text-foreground">Slides ready</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" className="rounded border-border" />
              <span className="text-foreground">CTAs configured</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" className="rounded border-border" />
              <span className="text-foreground">Handouts uploaded</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={cameraReady && micReady} readOnly className="rounded border-border" />
              <span className="text-foreground">Equipment tested</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={remindersStatus === "done"} readOnly className="rounded border-border" />
              <span className="text-foreground">Email reminders configured</span>
            </label>
          </div>
          {remindersStatus !== "done" && (
            <button
              onClick={handleSetupReminders}
              disabled={remindersStatus === "creating"}
              className="mt-3 w-full rounded-md border border-border px-3 py-2 text-sm text-foreground hover:bg-accent disabled:opacity-50"
            >
              {remindersStatus === "creating" ? "Setting up reminders..." : remindersStatus === "error" ? "Retry Setup Email Reminders" : "Setup Email Reminders"}
            </button>
          )}
          {remindersStatus === "done" && (
            <p className="mt-3 text-xs text-[var(--color-success)]">Pre-webinar + post-webinar email workflows created!</p>
          )}
        </div>
      </div>
    </div>
  );
}
